// Typed fetch client. Every API call goes through here so auth-token
// injection, JSON parsing, and error normalization are in one place.
//
// In dev: relative /api/* routes through Vite's proxy to the Go backend.
// In prod: relative /api/* hits the same origin (Go monolith serves both).

export class APIError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

const TOKEN_KEY = "taklifla.token";
export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t: string | null) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

// A request we authenticated with came back 401 — the stored session
// token is dead (server JWT secret rotated, or the 30-day TTL lapsed).
// Clear it and bounce to login, preserving where the user was via ?next=
// so they land back here after re-authenticating (LoginPage validates it).
// Without this a stale token strands the user on an "invalid token" error
// with no recovery path — most painfully at the checkout "Buy" step.
// Guarded so we never loop on the login page itself.
function onSessionExpired() {
  setToken(null);
  if (typeof window === "undefined") return;
  if (window.location.pathname === "/login") return;
  const next = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.assign(`/login?next=${next}`);
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  { quietAuth = false }: { quietAuth?: boolean } = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const err = body?.error ?? {};
    // Only treat a 401 as a dead session when WE sent a token — public
    // 401s and the pre-login auth calls (no token attached) fall through
    // as ordinary errors. account_disabled is a 403, so a banned user
    // keeps their message instead of being looped through login.
    if (res.status === 401 && token && !quietAuth) {
      onSessionExpired();
    }
    throw new APIError(
      res.status,
      err.code ?? "unknown",
      err.message ?? res.statusText,
    );
  }
  return body as T;
}

// ── Health ─────────────────────────────────────────────────────────────
export type Health = { status: string; build: string };
export const getHealth = () => request<Health>("/api/health");

// ── Auth ───────────────────────────────────────────────────────────────
// Email-OTP login: the user types their email, we mail them a 6-digit
// code, they submit it back. SMS login was removed when the Eskiz
// gateway was decommissioned; the `channel` knob is retained on the
// wire so future channels (e.g. magic link) can slot in without a
// breaking client refactor.
export const sendOTP = (target: string) =>
  request<{ sent: boolean }>("/api/v1/auth/otp/send", {
    method: "POST",
    body: JSON.stringify({ channel: "email", target }),
  });

export type VerifyResponse = { token: string; user_id: string };
export const verifyOTP = (target: string, code: string) =>
  request<VerifyResponse>("/api/v1/auth/otp/verify", {
    method: "POST",
    body: JSON.stringify({ channel: "email", target, code }),
  });

// Sign in with Google — exchanges a Google Identity Services ID token
// for our own JWT. Backend verifies the ID-token signature against
// Google's public JWKs and matches/links the user by `sub` first, then
// by email; see backend/internal/api/auth_handlers.go.
//
// Response shape mirrors VerifyResponse so the LoginPage can reuse the
// same setToken/redirect path.
export type GoogleSignInResponse = VerifyResponse & {
  new_user?: boolean;
  linked?: boolean;
};
export const signInWithGoogle = (idToken: string) =>
  request<GoogleSignInResponse>("/api/v1/auth/google", {
    method: "POST",
    body: JSON.stringify({ id_token: idToken }),
  });

// Sign in with Telegram — OIDC + PKCE. The frontend builds the PKCE
// pair, redirects to oauth.telegram.org/auth, then on the callback
// hands the code + verifier here. Backend POSTs to /token at Telegram
// (HTTP Basic with the client secret), verifies the returned id_token
// against Telegram's JWKS, upserts the user, and returns our JWT.
//
// Shape mirrors GoogleSignInResponse so the post-login redirect path
// is identical.
export type TelegramSignInResponse = VerifyResponse & {
  new_user?: boolean;
  linked?: boolean;
};
export const exchangeTelegramCode = (body: {
  code: string;
  code_verifier: string;
  redirect_uri: string;
}) =>
  request<TelegramSignInResponse>("/api/v1/auth/telegram/exchange", {
    method: "POST",
    body: JSON.stringify(body),
  });

// ── Me ─────────────────────────────────────────────────────────────────
export type Me = {
  id: string;
  phone?: string | null;
  email?: string | null;
  name?: string | null;
  locale: string;
};
export const getMe = () => request<Me>("/api/v1/me");
// Background probe (nav badge on every page): a dead token must not bounce
// the visitor to /login; the next call they actually make handles it.
export const probeMe = () => request<Me>("/api/v1/me", {}, { quietAuth: true });

// ── Public guest page ──────────────────────────────────────────────────
// Returned by GET /api/v1/public/i/:slug. Mirrors backend's publicInvitation
// (no user_id leaked).
export interface PublicInvitationPayload {
  names?: string;
  date?: string;
  venue?: string;
  time?: string;
  address?: string;
  /** Owner-supplied map link (Google Maps short URL, Yandex pin, etc.).
   *  When present, templates' map buttons open this URL verbatim. When
   *  absent, templates fall back to a `?q=<address>` Google search. */
  map_url?: string;
  photos?: string[];
  schedule?: Array<{ time: string; event: string }>;
  story?: string;
  custom?: Record<string, unknown>;
  // Promoted-from-custom (Phase C). Present on ~25 templates each, so they
  // get top-level type-checked support instead of living in `custom`.
  dress_code?: string;
  rsvp_deadline?: string;
  hashtag?: string;
  region?: string;
}
export interface PublicInvitation {
  id: string;
  slug: string;
  template_id: string;
  status: "draft" | "published" | "expired";
  payload: PublicInvitationPayload;
  music_track_id?: string | null;
  music_upload_id?: string | null;
  published_at?: string | null;
  expires_at?: string | null;
}
export const getPublicInvitation = (slug: string) =>
  request<PublicInvitation>(`/api/v1/public/i/${encodeURIComponent(slug)}`);

export interface PublicGuest { name: string; rsvp: "yes" | "no" | "maybe" | null }
export const getPublicGuests = (slug: string) =>
  request<PublicGuest[]>(`/api/v1/public/i/${encodeURIComponent(slug)}/guests`);

// Public read of the wishbook — returns every wish on a published
// invitation. Bypasses the HTTP cache (`cache: "no-store"`) so a
// freshly-deleted wish stops showing up on next visit instead of
// surviving until some opaque proxy TTL.
export interface PublicWish { name: string; message: string; created_at: string }
export const getPublicGuestbook = async (slug: string): Promise<PublicWish[]> => {
  const res = await fetch(`/api/v1/public/i/${encodeURIComponent(slug)}/guestbook`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) {
    const text = await res.text();
    const body = text ? JSON.parse(text) : {};
    throw new APIError(res.status, body?.error?.code ?? "unknown", body?.error?.message ?? res.statusText);
  }
  return res.json();
};

export interface RSVPInput {
  name: string;
  rsvp: "yes" | "no" | "maybe";
  party_size: number;
  message?: string;
}
export const postRSVP = (slug: string, body: RSVPInput) =>
  request<{ id: string }>(`/api/v1/public/i/${encodeURIComponent(slug)}/rsvp`, {
    method: "POST",
    body: JSON.stringify(body),
    credentials: "include",
  });

export const postGuestbook = (slug: string, body: { name: string; message: string }) =>
  request<{ id: string }>(`/api/v1/public/i/${encodeURIComponent(slug)}/guestbook`, {
    method: "POST",
    body: JSON.stringify(body),
    credentials: "include",
  });

// Render the human-friendly public URL for a published invitation.
// Backend assigns slugs of the form `<5-char-token>-<name-slug>` and the
// public route splits the first hyphen so the URL reads `/<token>/<name>`
// — `xk2j9-aziza-and-sardor` → `/xk2j9/aziza-and-sardor`. Falls back to
// the legacy `/i/<slug>` shape for any slug that doesn't match the
// pattern (older rows, hand-typed slugs, etc).
export function publicInvitationPath(slug: string): string {
  const m = /^([a-z2-9]{5})-(.+)$/.exec(slug);
  if (m) return `/${m[1]}/${m[2]}`;
  return `/i/${slug}`;
}

export function publicInvitationURL(slug: string): string {
  if (typeof window === "undefined") return publicInvitationPath(slug);
  return `${window.location.origin}${publicInvitationPath(slug)}`;
}

// ── Invitations (owner) ────────────────────────────────────────────────
// Mirrors backend/internal/domain/types.go Invitation. The owner-facing
// payload includes user_id; we keep the public payload type above since
// it diverges (no user_id, no timestamps).
export interface InvitationPayload extends PublicInvitationPayload {}

export interface Invitation {
  id: string;
  user_id: string;
  slug: string;
  template_id: string;
  status: "draft" | "published" | "expired";
  payload: InvitationPayload;
  music_track_id?: string | null;
  music_upload_id?: string | null;
  /** The owner's uploaded custom track id (resolved from music_upload_id by
   *  the GET handler). Set music_track_id to this value to select it; it
   *  plays at /music/<custom_music_id>.mp3. Present only after a custom
   *  upload — the editor uses it to offer the track + lock further uploads. */
  custom_music_id?: string | null;
  published_at?: string | null;
  expires_at?: string | null;
  // Server-authoritative paid bit (flipped inside the MarkPaid tx).
  // TemplateDetailPage uses it to swap "Buy" for "Open your invitation"
  // when the user already owns a paid invitation built from the template.
  paid?: boolean;
  paid_at?: string | null;
  // Anti-fraud window anchors. first_published_at is set once on the
  // FIRST publish and never reset. The editor uses it to render the
  // identity-lock countdown (names + date editable for 48h after this
  // moment) and the slug-change cap (3 changes within 7 days). The
  // backend enforces both gates in handlePatchInvitation.
  first_published_at?: string | null;
  slug_change_count?: number;
  created_at: string;
  updated_at: string;
}

/** Surface the anti-fraud window state in a single derived object the
 *  editor can render against. Pure function — no API call. */
export interface InvitationLockState {
  identityLocked: boolean;       // names + date are frozen
  identityUnlockedUntil: Date | null; // when the 48h grace ends (null pre-publish)
  slugFrozen: boolean;           // slug can no longer be changed
  slugChangesRemaining: number;  // 0..3 — what's left in the 7d window
}

const IDENTITY_GRACE_MS = 48 * 60 * 60 * 1000;
const SLUG_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const SLUG_CHANGE_CAP = 3;

export function computeLockState(inv: Invitation): InvitationLockState {
  if (!inv.first_published_at) {
    return {
      identityLocked: false,
      identityUnlockedUntil: null,
      slugFrozen: false,
      slugChangesRemaining: SLUG_CHANGE_CAP,
    };
  }
  const fp = new Date(inv.first_published_at).getTime();
  const now = Date.now();
  const used = inv.slug_change_count ?? 0;
  return {
    identityLocked: now > fp + IDENTITY_GRACE_MS,
    identityUnlockedUntil: new Date(fp + IDENTITY_GRACE_MS),
    slugFrozen: now > fp + SLUG_WINDOW_MS || used >= SLUG_CHANGE_CAP,
    slugChangesRemaining: Math.max(0, SLUG_CHANGE_CAP - used),
  };
}

export const listInvitations = () =>
  request<Invitation[]>("/api/v1/invitations");
// Background probe (guest-page owner chip, detail-page draft dedupe):
// same as listInvitations, but a dead token never redirects to /login.
export const probeInvitations = () =>
  request<Invitation[]>("/api/v1/invitations", {}, { quietAuth: true });

export interface CreateInvitationBody {
  template_id: string;
  slug?: string;
  payload: InvitationPayload;
  music_track_id?: string;
}

export const createInvitation = (body: CreateInvitationBody) =>
  request<Invitation>("/api/v1/invitations", {
    method: "POST",
    body: JSON.stringify(body),
  });

export const getInvitation = (id: string) =>
  request<Invitation>(`/api/v1/invitations/${encodeURIComponent(id)}`);

// Live slug-availability probe for the editor. `exclude` is the
// invitation being edited so its own current slug doesn't read as taken.
// Returns the server-normalised slug + whether it's free.
export interface SlugAvailability {
  slug: string;
  available: boolean;
  reason?: string;
}
export const checkSlugAvailable = (slug: string, excludeId?: string) => {
  const p = new URLSearchParams({ slug });
  if (excludeId) p.set("exclude", excludeId);
  return request<SlugAvailability>(`/api/v1/invitations/slug-available?${p.toString()}`);
};

export interface PatchInvitationBody {
  slug?: string;
  payload?: InvitationPayload;
  music_track_id?: string;
}

export const patchInvitation = (id: string, body: PatchInvitationBody) =>
  request<Invitation>(`/api/v1/invitations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });

export const publishInvitation = (id: string) =>
  request<Invitation>(`/api/v1/invitations/${encodeURIComponent(id)}/publish`, {
    method: "POST",
  });

// Take a published invitation private: the public link 404s but the
// invitation, wishes and paid entitlement survive; re-publish any time.
export const unpublishInvitation = (id: string) =>
  request<Invitation>(`/api/v1/invitations/${encodeURIComponent(id)}/unpublish`, {
    method: "POST",
  });

// Only unpaid drafts can be deleted (and server-side it's a recoverable
// soft-delete); the API answers 409 `not_deletable` for anything published
// or paid. Success is a bodyless 204.
export const deleteInvitation = (id: string) =>
  request<void>(`/api/v1/invitations/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });

// ── Guestbook moderation (owner) ───────────────────────────────────────
export interface GuestbookEntry {
  id: string;
  name: string;
  message: string;
  created_at: string;
}
export const listGuestbook = (invitationId: string) =>
  request<GuestbookEntry[]>(
    `/api/v1/invitations/${encodeURIComponent(invitationId)}/guestbook`,
  );
export const deleteGuestbookEntry = (invitationId: string, guestId: string) =>
  request<void>(
    `/api/v1/invitations/${encodeURIComponent(invitationId)}/guestbook/${encodeURIComponent(guestId)}`,
    { method: "DELETE" },
  );

// ── "Paid" gate (client-side mirror of the server-side `paid` bit) ─────
// We keep a localStorage flag keyed by invitation id so the editor /
// publish flow can render the unlocked UX synchronously, without waiting
// for a server roundtrip on every navigation. The authoritative source
// of truth is `invitations.paid` on the backend, flipped inside the
// same transaction that completes a Payme/Click order; this client cache
// is repopulated by the payment-return page once the gateway confirms.

const PAID_PREFIX = "taklifla.paid.";

export function isInvitationPaid(invitationId: string): boolean {
  try {
    return localStorage.getItem(PAID_PREFIX + invitationId) === "1";
  } catch {
    return false;
  }
}

export function markInvitationPaid(invitationId: string): void {
  try {
    localStorage.setItem(PAID_PREFIX + invitationId, "1");
  } catch {
    /* private mode — ignore */
  }
}

// ── Payments (Click.uz / Payme.uz) ─────────────────────────────────────
// These are the three calls the FE needs:
//   1. getPaymentsConfig — read the feature flag so checkout can decide
//      whether to render the password field (legacy) or gateway buttons.
//   2. createPaymentOrder — body { invitation_id, provider } → returns
//      a redirect URL. FE does window.location = redirect_url.
//   3. getPaymentOrder — polled by the return-from-gateway page until
//      status moves out of 'pending'.
export type PaymentProvider = "none" | "click" | "payme" | "both";
export interface PaymentsConfig {
  provider: PaymentProvider;
  invitation_price_tiyin: number;
  invitation_price_som: number;
  // Lemon Squeezy (international / .com card checkout). Independent of
  // `provider` (which governs the Uzbek gateways).
  lemon_available: boolean;
}
export const getPaymentsConfig = () => request<PaymentsConfig>("/api/v1/payments/config");

export interface CreateOrderInput {
  invitation_id: string;
  provider: "click" | "payme" | "lemonsqueezy";
}
export interface CreatedOrder {
  order_id: string;
  provider: string;
  redirect_url: string;
  amount_tiyin: number;
}
export const createPaymentOrder = (body: CreateOrderInput) =>
  request<CreatedOrder>("/api/v1/payments/orders", {
    method: "POST",
    body: JSON.stringify(body),
  });

export interface PaymentOrder {
  order_id: string;
  invitation_id: string;
  status: "pending" | "paid" | "cancelled" | "failed" | "refunded";
  provider?: string | null;
  amount_tiyin: number;
  currency: string;
  paid_at?: string | null;
}
export const getPaymentOrder = (orderId: string) =>
  request<PaymentOrder>(`/api/v1/payments/orders/${encodeURIComponent(orderId)}`);

// ── Template visibility (operator CMS) ─────────────────────────────────
// The dashboard can hide individual templates from the public catalog
// (e.g. seasonal, deprecated, or temporarily withdrawn). The map only
// contains operator-touched ids — anything missing is is_public=true.

// Operator-managed override for one template. Each field is nullable;
// NULL means "use the default baked into data.ts", a present value
// overrides it at runtime via mergeTemplate (below).
export interface TemplateOverrideRow {
  template_id: string;
  is_public: boolean;
  title?: string | null;
  category?: string | null;
  price_tiyin?: number | null;
  badge?: string | null;
  bg?: string | null;
  ink?: string | null;
  accent?: string | null;
  font?: string | null;
  demo_names?: string | null;
  demo_date?: string | null;
  demo_venue?: string | null;
}

// Back-compat alias.
export type TemplateVisibility = TemplateOverrideRow;

export const fetchTemplateVisibility = () =>
  request<{ visibility: TemplateOverrideRow[] }>("/api/v1/templates/visibility");

// Convenience: returns a Set of template_ids that are explicitly hidden.
// Callers default-to-visible for ids not in the set.
export async function fetchHiddenTemplateIds(): Promise<Set<string>> {
  try {
    const { visibility } = await fetchTemplateVisibility();
    return new Set(visibility.filter((v) => !v.is_public).map((v) => v.template_id));
  } catch {
    return new Set();
  }
}

// Returns the full override map keyed by template_id. Callers merge
// onto their hardcoded TEMPLATES rows. Failure → empty map.
export async function fetchTemplateOverrides(): Promise<
  Map<string, TemplateOverrideRow>
> {
  try {
    const { visibility } = await fetchTemplateVisibility();
    return new Map(visibility.map((v) => [v.template_id, v]));
  } catch {
    return new Map();
  }
}

// ── Music tracks (curated catalog) ─────────────────────────────────────
// Curated stock music users can attach to invitations. The catalog is
// served by the backend; if that endpoint isn't reachable, callers
// (e.g. MusicSelector) fall back to a hard-coded mirror of the seed.
export interface MusicTrack {
  id: string;
  title: string;
  title_uz?: string;
  title_ru?: string;
  category: string;
  duration_seconds: number;
  url: string;
  is_premium: boolean;
}
export const listMusicTracks = (category?: string) => {
  const qs = category ? `?category=${encodeURIComponent(category)}` : "";
  return request<MusicTrack[]>(`/api/v1/music/tracks${qs}`);
};

// ── Uploads ────────────────────────────────────────────────────────────
// Multipart upload bypasses the JSON-only `request` helper above because
// the browser must own the Content-Type header (it sets the multipart
// boundary). We still hand-roll auth + APIError to stay consistent.
export interface UploadResult {
  id: string;
  path: string;
  mime_type: string;
  size_bytes: number;
  url: string;
}
export async function uploadPhoto(file: File, invitationId?: string): Promise<UploadResult> {
  const fd = new FormData();
  fd.append("file", file);
  if (invitationId) fd.append("invitation_id", invitationId);
  // Don't set content-type — browser sets multipart boundary
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch("/api/v1/uploads/photo", { method: "POST", body: fd, headers });
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new APIError(
      res.status,
      body?.error?.code ?? "upload_failed",
      body?.error?.message ?? res.statusText,
    );
  }
  return body as UploadResult;
}

// Upload ONE custom soundtrack (MP3) for an invitation the user owns. The
// backend enforces once-per-invitation (409 already_uploaded on a second
// try) and selects the track (sets music_track_id = custom_music_id). Same
// multipart/auth shape as uploadPhoto.
export interface MusicUploadResult {
  custom_music_id: string;
  music_upload_id: string;
  url: string;
}
export async function uploadInvitationMusic(
  invitationId: string,
  file: File,
): Promise<MusicUploadResult> {
  const fd = new FormData();
  fd.append("file", file);
  const headers = new Headers();
  const token = getToken();
  if (token) headers.set("authorization", `Bearer ${token}`);
  const res = await fetch(
    `/api/v1/invitations/${encodeURIComponent(invitationId)}/music`,
    { method: "POST", body: fd, headers },
  );
  const text = await res.text();
  const body = text ? JSON.parse(text) : {};
  if (!res.ok) {
    throw new APIError(
      res.status,
      body?.error?.code ?? "upload_failed",
      body?.error?.message ?? res.statusText,
    );
  }
  return body as MusicUploadResult;
}
